/**
 * The one message the chrome layer sends *to* the frontend. The cold client cannot log an exposure
 * (logging off, no network), so it names the key and the frontend's live client logs it. Shared here
 * because both halves must agree on the string and neither imports the other.
 */
export const LOG_FEATURE_GATE_EXPOSURE_MESSAGE_TYPE = 'LOG_FEATURE_GATE_EXPOSURE';

/**
 * The frontend route the extension's hidden relay page loads to log a surface's exposure when no
 * frontend is mounted beside it. Renders nothing, and only initialises the live Statsig client.
 */
export const EXPOSURE_RELAY_PATH = '/extension/exposure-relay';

/** What a relayed exposure is for, so the frontend logs it with the matching SDK call. */
export type ExposureKind = 'experiment' | 'gate';

export interface LogFeatureGateExposureMessage {
	readonly type: typeof LOG_FEATURE_GATE_EXPOSURE_MESSAGE_TYPE;
	/**
	 * An experiment key (a registered one, or one the frontend owns and the extension relays), or a
	 * gate not registered `relayExposure: false`. Never a dynamic config, which needs no exposure.
	 */
	readonly key: string;
	/**
	 * Absent means `experiment`: older extensions and the legacy right-click path send none. The
	 * frontend deploys before the extension that sends `gate`, so it always understands it.
	 */
	readonly kind?: ExposureKind;
}

export function isLogFeatureGateExposureMessage(
	data: unknown,
): data is LogFeatureGateExposureMessage {
	if (typeof data !== 'object' || data === null) {
		return false;
	}
	const { type, key, kind } = data as { type?: unknown; key?: unknown; kind?: unknown };
	return (
		type === LOG_FEATURE_GATE_EXPOSURE_MESSAGE_TYPE &&
		typeof key === 'string' &&
		(kind === undefined || kind === 'experiment' || kind === 'gate')
	);
}

/**
 * Sent by the extension's hidden relay page after the last exposure message, so the frontend flushes
 * its event queue before the page closes. Without it an exposure waits for the SDK's flush interval.
 */
export const FLUSH_FEATURE_GATE_EXPOSURES_MESSAGE_TYPE = 'FLUSH_FEATURE_GATE_EXPOSURES';

export interface FlushFeatureGateExposuresMessage {
	readonly type: typeof FLUSH_FEATURE_GATE_EXPOSURES_MESSAGE_TYPE;
}

export function isFlushFeatureGateExposuresMessage(
	data: unknown,
): data is FlushFeatureGateExposuresMessage {
	return (
		typeof data === 'object' &&
		data !== null &&
		(data as { type?: unknown }).type === FLUSH_FEATURE_GATE_EXPOSURES_MESSAGE_TYPE
	);
}
